import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'sonner';
import { Download, LogOut, Share, ShieldCheck, TicketCheck } from 'lucide-react';
import { Dialog, DialogContent, DialogDescription, DialogTitle } from '@/components/ui/dialog';
import { A, fmtUsd } from '@/constants/journey';
import { resolveTicket } from '@/lib/api';
import { connectWallet, disconnectWallet, fetchSolanaTokenBalance, getSolanaWallets, initWalletDiscovery } from '@/lib/wallet';
import { useLive } from '@/lib/live';

function loadImage(src) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = src;
  });
}

function configured(value) {
  return Boolean(value && !String(value).startsWith('{{'));
}

function issuedDate(iso) {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '13 AUG 2026';
  return date.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', timeZone: 'UTC' }).toUpperCase();
}

function balanceLabel(ticket, ticker) {
  if (typeof ticket.tokenBalance !== 'number') return 'HOLDER STAMP READY';
  const amount = new Intl.NumberFormat('en-US', { maximumFractionDigits: 2 }).format(ticket.tokenBalance);
  return `${amount} ${ticker}`;
}

async function renderTicket({ layout, ticket, tickerText }) {
  const isLandscape = layout === 'landscape';
  const W = isLandscape ? 1600 : 1080;
  const H = isLandscape ? 900 : 1350;
  const canvas = document.createElement('canvas');
  canvas.width = W;
  canvas.height = H;
  const ctx = canvas.getContext('2d');
  if (document.fonts?.ready) await document.fonts.ready;

  const backdrop = ctx.createLinearGradient(0, 0, W, H);
  backdrop.addColorStop(0, '#101722');
  backdrop.addColorStop(0.52, '#0a0d12');
  backdrop.addColorStop(1, '#182233');
  ctx.fillStyle = backdrop;
  ctx.fillRect(0, 0, W, H);

  const glow = ctx.createRadialGradient(W * 0.5, H * 0.44, 30, W * 0.5, H * 0.44, W * 0.7);
  glow.addColorStop(0, 'rgba(201,162,74,0.16)');
  glow.addColorStop(1, 'rgba(201,162,74,0)');
  ctx.fillStyle = glow;
  ctx.fillRect(0, 0, W, H);

  const template = await loadImage(isLandscape ? A.tickets.landscape : A.tickets.portrait);
  const scale = isLandscape ? Math.min(1180 / template.width, 820 / template.height) : Math.min(760 / template.width, 1325 / template.height);
  const tw = template.width * scale;
  const th = template.height * scale;
  const tx = (W - tw) / 2;
  const ty = (H - th) / 2;
  ctx.shadowColor = 'rgba(0,0,0,.58)';
  ctx.shadowBlur = isLandscape ? 42 : 34;
  ctx.shadowOffsetY = 18;
  ctx.drawImage(template, tx, ty, tw, th);
  ctx.shadowColor = 'transparent';

  const ink = '#2a2115';
  const muted = '#76654b';
  const red = '#842432';
  const cream = '#f3e7cf';
  const font = (size, weight = 500, family = 'IBM Plex Mono') => `${weight} ${Math.round(size)}px '${family}', monospace`;

  const fit = (text, x, y, maxWidth, size, options = {}) => {
    const { color = ink, weight = 600, family = 'IBM Plex Mono', align = 'center', min = 13 } = options;
    let current = size;
    ctx.font = font(current, weight, family);
    while (ctx.measureText(String(text)).width > maxWidth && current > min) {
      current -= 1;
      ctx.font = font(current, weight, family);
    }
    ctx.fillStyle = color;
    ctx.textAlign = align;
    ctx.textBaseline = 'alphabetic';
    ctx.fillText(String(text), x, y);
  };

  const label = (text, x, y, size) => fit(text, x, y, isLandscape ? tw * 0.18 : tw * 0.7, size, { color: muted, weight: 500 });
  const routePercent = `${Math.round((ticket.routeProgress || 0) * 100)}% OF LINE`;
  const station = ticket.stationName || 'THOMAS DEPOT';
  const status = ticket.boardingStatus || 'TICKET ISSUED';

  if (isLandscape) {
    fit('THOMAS COIN · PASSENGER TICKET', tx + tw * 0.525, ty + th * 0.135, tw * 0.42, th * 0.041, { color: red, family: 'Oswald' });
    fit(`SOLANA RAILWAY · $${tickerText}`, tx + tw * 0.525, ty + th * 0.18, tw * 0.38, th * 0.025, { color: muted, weight: 500 });

    fit('THOMAS DEPOT', tx + tw * 0.325, ty + th * 0.355, tw * 0.24, th * 0.027, { family: 'Oswald' });
    fit(station, tx + tw * 0.704, ty + th * 0.355, tw * 0.25, th * 0.027, { family: 'Oswald' });

    const rowY = ty + th * 0.596;
    const columns = [
      { x: 0.18, label: 'PASSENGER', value: ticket.publicKeyShort, width: 0.19 },
      { x: 0.4, label: 'COACH · SEAT', value: `${ticket.coach} · ${ticket.seat}`, width: 0.2 },
      { x: 0.605, label: 'TICKET NO.', value: ticket.serial, width: 0.17 },
    ];
    columns.forEach((column) => {
      label(column.label, tx + tw * column.x, rowY - th * 0.055, th * 0.019);
      fit(column.value, tx + tw * column.x, rowY, tw * column.width, th * 0.028, { min: 15 });
    });

    fit(`${routePercent} · ATH ${fmtUsd(ticket.verifiedAthMcapUsd)}`, tx + tw * 0.4, rowY + th * 0.09, tw * 0.57, th * 0.021, { color: muted, weight: 500 });
    fit(`${status} · ${issuedDate(ticket.issuedAt)}`, tx + tw * 0.4, ty + th * 0.885, tw * 0.61, th * 0.027, { color: cream, min: 16 });

    ctx.save();
    ctx.translate(tx + tw * 0.815, ty + th * 0.52);
    ctx.rotate(-Math.PI / 2);
    fit(balanceLabel(ticket, tickerText), 0, 0, th * 0.55, th * 0.02, { color: muted, weight: 500 });
    ctx.restore();
  } else {
    // The medallion is the portrait header. Text starts below it so the brand
    // never collides with Thomas' face on narrow mobile exports.
    fit(`PASSENGER TICKET · $${tickerText}`, tx + tw * 0.5, ty + th * 0.255, tw * 0.62, th * 0.017, { color: red, weight: 600 });

    fit('THOMAS DEPOT', tx + tw * 0.275, ty + th * 0.365, tw * 0.29, th * 0.015, { family: 'Oswald' });
    fit(station, tx + tw * 0.72, ty + th * 0.365, tw * 0.3, th * 0.015, { family: 'Oswald' });

    const columns = [
      [0.25, 'PASSENGER', ticket.publicKeyShort, 0.25],
      [0.5, 'COACH · SEAT', `${ticket.coach} · ${ticket.seat}`, 0.26],
      [0.76, 'TICKET NO.', ticket.serial, 0.22],
    ];
    columns.forEach(([position, columnLabel, value, width]) => {
      label(columnLabel, tx + tw * position, ty + th * 0.525, th * 0.012);
      fit(value, tx + tw * position, ty + th * 0.565, tw * width, th * 0.017, { min: 12 });
    });
    fit(`${routePercent} · ATH ${fmtUsd(ticket.verifiedAthMcapUsd)}`, tx + tw * 0.5, ty + th * 0.65, tw * 0.67, th * 0.015, { color: muted, weight: 500, min: 11 });
    fit(`${status} · ${issuedDate(ticket.issuedAt)}`, tx + tw * 0.5, ty + th * 0.79, tw * 0.7, th * 0.018, { color: cream, min: 13 });
    fit(balanceLabel(ticket, tickerText), tx + tw * 0.5, ty + th * 0.91, tw * 0.6, th * 0.013, { color: cream, weight: 500, min: 11 });
  }

  return canvas;
}

export const TicketModal = () => {
  const { ticketOpen, setTicketOpen, config, state } = useLive();
  const [wallets, setWallets] = useState([]);
  const [connecting, setConnecting] = useState(false);
  const [entry, setEntry] = useState(null);
  const [ticket, setTicket] = useState(null);
  const [previewUrl, setPreviewUrl] = useState('');
  const fullAddress = useRef('');
  const ticker = config?.placeholders?.TOKEN_TICKER || 'THOMAS';
  const mintConfigured = configured(config?.placeholders?.TOKEN_MINT_ADDRESS || config?.placeholders?.CONTRACT_ADDRESS);

  const localTicket = useCallback(async (address) => {
    const bytes = new TextEncoder().encode(address);
    const digest = await window.crypto.subtle.digest('SHA-256', bytes);
    const hex = Array.from(new Uint8Array(digest), (byte) => byte.toString(16).padStart(2, '0')).join('');
    const coachIndex = parseInt(hex.slice(0, 8), 16) % 3;
    const seat = parseInt(hex.slice(8, 12), 16) % 48 + 1;
    const mint = config?.placeholders?.TOKEN_MINT_ADDRESS || config?.placeholders?.CONTRACT_ADDRESS;
    let tokenBalance = null;
    if (mintConfigured) {
      try { tokenBalance = await fetchSolanaTokenBalance(address, mint); } catch {}
    }
    const coaches = ['RED COACH I', 'GREEN COACH II', 'RED COACH III'];
    return {
      publicKeyShort: `${address.slice(0, 4)}…${address.slice(-4)}`,
      coach: coaches[coachIndex],
      seat,
      serial: `TC-${hex.slice(12, 18).toUpperCase()}`,
      tokenBalance,
      boardingStatus: tokenBalance > 0 ? 'HOLDER ABOARD' : 'TICKET ISSUED',
      stationName: state?.stationName || 'THOMAS DEPOT',
      routeProgress: state?.routeProgress || 0,
      verifiedAthMcapUsd: state?.verifiedAthMcapUsd || null,
      issuedAt: new Date().toISOString(),
      mintConfigured,
    };
  }, [config, mintConfigured, state]);

  useEffect(() => {
    initWalletDiscovery();
    const refresh = () => setWallets(getSolanaWallets());
    window.addEventListener('thomas:wallets-changed', refresh);
    return () => window.removeEventListener('thomas:wallets-changed', refresh);
  }, []);

  useEffect(() => {
    if (ticketOpen) setWallets(getSolanaWallets());
    else {
      setPreviewUrl('');
      setTicket(null);
      setEntry(null);
      fullAddress.current = '';
    }
  }, [ticketOpen]);

  useEffect(() => {
    let current = true;
    if (!ticket) return undefined;
    const layout = window.innerWidth < 640 ? 'portrait' : 'landscape';
    renderTicket({ layout, ticket, tickerText: ticker })
      .then((canvas) => { if (current) setPreviewUrl(canvas.toDataURL('image/png')); })
      .catch(() => toast('TICKET OFFICE TEMPORARILY CLOSED.', { description: 'Please reopen the ticket window.' }));
    return () => { current = false; };
  }, [ticket, ticker]);

  const handleConnect = async (wallet) => {
    setConnecting(true);
    try {
      const address = await connectWallet(wallet);
      fullAddress.current = address;
      let issued;
      try {
        issued = await resolveTicket(address);
      } catch {
        issued = await localTicket(address);
      }
      setEntry(wallet);
      setTicket({ ...issued, boardingStatus: issued.boardingStatus || 'TICKET ISSUED' });
      toast('TICKET ISSUED.', { description: `${issued.publicKeyShort} · ${issued.coach} · SEAT ${issued.seat}` });
    } catch {
      toast('NO TICKET ISSUED.', { description: 'The wallet connection was declined. No signature or transaction was requested.' });
    } finally {
      setConnecting(false);
    }
  };

  const handleDisconnect = async () => {
    await disconnectWallet(entry);
    setEntry(null);
    setTicket(null);
    setPreviewUrl('');
    fullAddress.current = '';
    toast('WALLET DISCONNECTED.', { description: 'The ticket image remains yours if you downloaded it.' });
  };

  const download = async (layout) => {
    if (!ticket) return;
    try {
      const canvas = await renderTicket({ layout, ticket, tickerText: ticker });
      const link = document.createElement('a');
      link.download = `thomas-ticket-${ticket.serial}-${layout}.png`;
      link.href = canvas.toDataURL('image/png');
      link.click();
      toast('TICKET SAVED.', { description: layout === 'landscape' ? '1600×900 PNG' : '1080×1350 PNG' });
    } catch {
      toast('EXPORT FAILED.', { description: 'Please try the ticket office again.' });
    }
  };

  const shareX = () => {
    if (!ticket) return;
    const text = encodeURIComponent(`My $${ticker} passenger ticket is issued: ${ticket.serial} · ${ticket.coach} · Seat ${ticket.seat}. Board the Thomas Coin railway.`);
    window.open(`https://x.com/intent/tweet?text=${text}`, '_blank', 'noopener,noreferrer');
  };

  const readinessText = useMemo(() => (
    mintConfigured
      ? 'The official mint is connected. Ticket issue and holder stamp are live.'
      : 'Ticket issue is live. Holder-balance status is temporarily unavailable.'
  ), [mintConfigured]);

  return (
    <Dialog open={ticketOpen} onOpenChange={setTicketOpen}>
      <DialogContent
        className="ticket-office max-h-[92vh] max-w-3xl overflow-y-auto border-[#c9a24a]/25 bg-[#090c11] p-0 text-[#f3e7cf]"
        data-testid="ticket-modal"
      >
        <div className="p-5 sm:p-7">
          <div className="pr-8">
            <div className="mb-2 flex items-center gap-2 font-mono text-[10px] tracking-[0.22em] text-[#c9a24a]">
              <TicketCheck size={14} /> THOMAS PASSENGER OFFICE
            </div>
            <DialogTitle asChild>
              <h3 className="font-display text-4xl tracking-wide text-[#f3e7cf] sm:text-5xl">GET YOUR TRAIN TICKET.</h3>
            </DialogTitle>
            <DialogDescription className="mt-3 max-w-2xl font-sans text-sm leading-6 text-[#f3e7cf]/74 sm:text-[15px]">
              Connect a Solana wallet and your numbered Thomas passenger ticket is issued instantly with a coach and seat. We only read the public address—there is no transaction, signature, approval or wallet charge.
            </DialogDescription>
          </div>

          {!ticket ? (
            <div className="mt-6 space-y-4">
              <div className="ticket-readiness flex items-start gap-3 rounded-xl px-4 py-3">
                <ShieldCheck className="mt-0.5 shrink-0 text-[#c9a24a]" size={17} />
                <p className="font-sans text-[13px] leading-5 text-[#f3e7cf]/72">{readinessText}</p>
              </div>

              {wallets.length > 0 ? (
                <div className="grid gap-2.5 sm:grid-cols-2">
                  {wallets.map((wallet) => (
                    <button
                      key={wallet.name}
                      type="button"
                      disabled={connecting}
                      onClick={() => handleConnect(wallet)}
                      className="ticket-wallet-button btn-mech flex min-h-14 items-center justify-between rounded-xl px-4 text-left disabled:opacity-50"
                      data-testid="ticket-connect-button"
                    >
                      <span className="flex items-center gap-3 font-sans text-sm font-semibold text-[#f3e7cf]">
                        {wallet.icon ? <img src={wallet.icon} alt="" className="h-7 w-7 rounded-md" /> : <span className="flex h-7 w-7 items-center justify-center rounded-full border border-[#c9a24a]/45 text-[#c9a24a]">T</span>}
                        {wallet.name}
                      </span>
                      <span className="font-mono text-[9px] tracking-[0.14em] text-[#c9a24a]">{connecting ? 'CONNECTING…' : 'ISSUE TICKET'}</span>
                    </button>
                  ))}
                </div>
              ) : (
                <div className="ticket-wallet-empty rounded-xl p-4 sm:p-5">
                  <p className="font-sans text-sm font-semibold text-[#f3e7cf]">No Solana wallet found in this browser.</p>
                  <p className="mt-1 font-sans text-[13px] leading-5 text-[#f3e7cf]/60">Open this page in Phantom, Solflare or Backpack to issue your numbered passenger ticket.</p>
                  <div className="mt-4 flex flex-wrap gap-4 font-mono text-[10px] tracking-[0.12em] text-[#c9a24a]">
                    <a href="https://phantom.com/download" target="_blank" rel="noreferrer">PHANTOM ↗</a>
                    <a href="https://solflare.com" target="_blank" rel="noreferrer">SOLFLARE ↗</a>
                    <a href="https://backpack.app" target="_blank" rel="noreferrer">BACKPACK ↗</a>
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div className="mt-6 space-y-4">
              <div className="ticket-issued-heading flex flex-wrap items-center justify-between gap-2 border-y border-[#c9a24a]/20 py-3">
                <span className="flex items-center gap-2 font-mono text-[10px] tracking-[0.18em] text-[#c9a24a]"><TicketCheck size={14} /> TICKET ISSUED</span>
                <span className="font-mono text-[10px] tracking-[0.1em] text-[#f3e7cf]/55">{ticket.serial} · {ticket.publicKeyShort}</span>
              </div>

              <div className="ticket-preview-shell" data-testid="ticket-preview">
                {previewUrl ? <img src={previewUrl} alt={`Issued Thomas passenger ticket ${ticket.serial}`} className="block h-auto w-full" /> : <div className="aspect-[16/9] animate-pulse bg-white/5" />}
              </div>

              <p className="font-sans text-[13px] leading-5 text-[#f3e7cf]/66">
                {typeof ticket.tokenBalance === 'number'
                  ? `Holder stamp checked: ${balanceLabel(ticket, ticker)}.`
                  : 'Your ticket is fully issued. Holder balance could not be read right now.'}
              </p>

              <div className="grid grid-cols-2 gap-2.5 sm:grid-cols-4">
                <button type="button" onClick={() => download('landscape')} className="ticket-action btn-mech flex h-12 items-center justify-center gap-2 rounded-lg font-mono text-[10px] tracking-[0.08em]" data-testid="ticket-download-landscape"><Download size={14} /> LANDSCAPE</button>
                <button type="button" onClick={() => download('portrait')} className="ticket-action btn-mech flex h-12 items-center justify-center gap-2 rounded-lg font-mono text-[10px] tracking-[0.08em]" data-testid="ticket-download-portrait"><Download size={14} /> PORTRAIT</button>
                <button type="button" onClick={shareX} className="ticket-action btn-mech flex h-12 items-center justify-center gap-2 rounded-lg font-mono text-[10px] tracking-[0.08em]" data-testid="ticket-share-x"><Share size={14} /> SHARE ON X</button>
                <button type="button" onClick={handleDisconnect} className="ticket-action btn-mech flex h-12 items-center justify-center gap-2 rounded-lg font-mono text-[10px] tracking-[0.08em]" data-testid="ticket-disconnect-button"><LogOut size={14} /> DISCONNECT</button>
              </div>

              <p className="font-sans text-[11px] leading-5 text-[#f3e7cf]/38">The exported ticket contains only the shortened public address. Downloading and sharing are always your explicit action.</p>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};
